/** This composable is logic for fetching and saving BOMs to hide the complexity of local storage vs. API */
import * as v from "valibot";
import type { FetchError } from "ofetch";

const VALID_EXTENSIONS = ["xls", "xlsx", "csv", "tsv"];

type UploadCompleteResponse = {
    truncated: boolean;
    res?: BOMSearch; // only if it's anonymous :-/
    Id?: string; // only if it's not anonymous
};

function getNextPartId(bom: BOMSearch) {
    if (!Array.isArray(bom.Parts) || bom.Parts.length == 0) {
        return 1;
    }
    const maxId = bom.Parts.reduce((prev, current) => (prev.Id > current.Id ? prev : current)).Id;
    return isNaN(maxId) ? 1 : maxId + 1;
}

export default function useBom() {
    const { t } = useI18n();
    const { $api } = useNuxtApp();
    const isAuthenticated = useStateIsAuthenticated();
    const { BomNameSchema } = useSchemas();

    async function fetchBOMs(query?: Record<string, string | number | boolean | undefined>) {
        if (isAuthenticated.value) {
            return $api<BOMSearch[]>("bom", { query });
        } else {
            const localBOM = fetchAnonymousBOM();
            return localBOM ? [localBOM] : [];
        }
    }

    function fetchAnonymousBOM(): BOMSearch | null {
        const bomString = localStorage.getItem(BOM_TOOL.STORAGE_KEY);
        if (!bomString) return null;
        try {
            const localBOM = JSON.parse(bomString);
            return { ...localBOM, HashId: "local" };
        } catch (_err) {
            return null; // invalid JSON
        }
    }

    async function getDefaultBOM() {
        return $api<BOMSearch>("bom/default");
    }

    async function saveBOM(bom: BOMSearch, skipParts: boolean = false): Promise<BOMSearch> {
        // fix sort num indexes
        if (!skipParts) {
            bom.Parts.forEach((part, index) => {
                part.SortNum = index + 1;
            });
        }

        if (bom.BOMPermissionLevel === BOMPermissionLevel.Sample) return bom;

        if (isAuthenticated.value) {
            const partialBOM = getPartialBom(bom, skipParts);
            // The API does not return the full BOM object, so we need to merge the output
            if (bom.HashId) {
                const updatedBOM = await $api<BOMSearch>(`bom/${encodeURI(bom.HashId)}`, {
                    method: "PUT",
                    body: partialBOM,
                });
                return { ...bom, ...updatedBOM };
            } else {
                const newBOM = await $api<BOMSearch>("bom", { method: "POST", body: partialBOM });
                return { ...bom, ...newBOM };
            }
        } else {
            const isReadOnly =
                bom.IsLocked ||
                bom.BOMPermissionLevel == BOMPermissionLevel.Public ||
                bom.BOMPermissionLevel == BOMPermissionLevel.Viewer;

            if (isReadOnly) return bom; // make sure not to add a shared BOM to local storage accidentally
            localStorage.setItem(BOM_TOOL.STORAGE_KEY, JSON.stringify(bom));
            return { ...bom, HashId: "local" };
        }
    }

    //Creates a new BOM with just a name and optionally the first part
    async function createBOM(name: string, part?: Partial<BOMSearchPart>) {
        const parsedName = v.parse(BomNameSchema, name);

        let newBOM = await getDefaultBOM();
        newBOM.Name = parsedName;
        if (part) newBOM.Parts = [];
        newBOM = await saveBOM(newBOM);
        return part ? saveBOMPart(newBOM, part) : newBOM;
    }

    // Given an existing BOM and a part to search for, returns a new copy of the BOM with a complete part from the API
    async function saveBOMPart(bom: BOMSearch, part: Partial<BOMSearchPart>): Promise<BOMSearch> {
        const isCreating = part.Id === undefined; // we can't rely on !!part.Id because the first guest BOM part ID = 0

        if (!isAuthenticated.value && bom.Parts.length >= BOM_TOOL.MAX_PARTS && isCreating) {
            throw new Error(t("BOMTool.AddTooManyPartsWarning"));
        }

        const isSample = bom.BOMPermissionLevel === BOMPermissionLevel.Sample;
        const method = isCreating ? "POST" : "PUT";
        const url = `bom/${encodeURI(bom.HashId)}/part` + (isCreating ? "" : "/" + part.Id);

        // make a shallow copy of BOM and part to avoid mutating
        const newBOM: BOMSearch = { ...bom };
        const newPart: Partial<BOMSearchPart> = { ...part };

        if (!Array.isArray(newBOM.Parts)) newBOM.Parts = [];
        if (newPart.SortNum === undefined || newPart.SortNum < 1) newPart.SortNum = newBOM.Parts.length + 1;

        const completePart =
            !isAuthenticated.value && part.PartId !== undefined
                ? (newPart as BOMSearchPart) // guest user already has the complete part no need to fetch again
                : await $api<BOMSearchPart>(url, { method, body: getPartialPart(newPart) });

        if (isCreating) {
            if (!isAuthenticated.value) completePart.Id = getNextPartId(newBOM);
            newBOM.SortOrder = BOMSortOrder.Default;
        }

        const partIndex = isCreating ? -1 : newBOM.Parts.findIndex((p) => p.Id == part.Id);
        if (partIndex >= 0) {
            newBOM.Parts.splice(partIndex, 1, completePart); // updating
        } else {
            newBOM.Parts.push(completePart); // adding
        }

        if (isAuthenticated.value && !isCreating && !isSample) return { ...newBOM, PartExists: true }; // API already linked the part, don't save again

        // for anon users, or for a new part, we need to store the complete BOM with part
        const savedBOM = await saveBOM(newBOM);
        return { ...savedBOM, PartExists: true };
    }

    async function removeBOMPart(bom: BOMSearch, part: BOMSearchPart): Promise<BOMSearch> {
        const newBOM: BOMSearch = { ...bom, Parts: bom.Parts.filter((p) => p.Id != part.Id) };

        if (isAuthenticated.value && bom.BOMPermissionLevel !== BOMPermissionLevel.Sample) {
            const url = `bom/${encodeURI(bom.HashId)}/part/${part.Id}`;
            await $api(url, { method: "DELETE" });
        }
        return saveBOM(newBOM); // this will update part SortNum
    }

    // Imports the anonymous BOM from local storage to the API, to be used just after login.
    async function importAnonymousBOM() {
        if (!isAuthenticated.value) return;
        const anonBOM = fetchAnonymousBOM() as BOMSearch;
        if (!anonBOM) return;
        try {
            anonBOM.HashId = ""; // no longer "local", this will create a new record
            const newBOM = await saveBOM(anonBOM);
            localStorage.removeItem(BOM_TOOL.STORAGE_KEY); // delete it so we don't keep importing it

            // get new part IDs by key; downside is that it won't account for duplicate parts
            const newPartsByKey: Record<string, BOMSearchPart> = {};
            for (const newPart of newBOM.Parts) {
                newPartsByKey[newPart.Key] = newPart;
            }

            // import selections one a time; they are not sent in a batch
            const promises: Promise<{ Id: number }>[] = [];
            const createSelectionUrl = `bom/${encodeURI(newBOM.HashId)}/part-selection`;

            for (const anonPart of anonBOM.Parts) {
                for (const selection of anonPart.Selections ?? []) {
                    const newPart = newPartsByKey[anonPart.Key];
                    if (newPart) {
                        const body: BOMSearchSelection = { ...selection, BOMsearchPartId: newPart.Id };
                        promises.push($api<{ Id: number }>(createSelectionUrl, { method: "POST", body }));
                    }
                }
            }
            await Promise.allSettled(promises);
        } catch (err) {
            // ignore error in UI; importing is best-effort
            console.error("importAnonymousBOM", err);
        }
    }

    function partExistsInBOM(bom: BOMSearch | null, part: Partial<BOMSearchPart>) {
        return bom ? bom.Parts.some((bomPart) => bomPart.Key === part.Key || bomPart.PartId === part.PartId) : false;
    }

    async function uploadStart(file?: File) {
        if (!file) return;

        const ext = file.name.split(".").pop();
        if (!ext || !VALID_EXTENSIONS.includes(ext.toLowerCase())) {
            throw new Error(t("BOMTool.InvalidFormat"));
        }

        const fileSizeMb = file.size / 1_048_576;
        if (fileSizeMb > MAX_FILE_UPLOAD_MEGABYTES) {
            throw new Error(t("Global.MaxFileUploadSize", [MAX_FILE_UPLOAD_MEGABYTES]));
        }

        try {
            const body = new FormData();
            body.append("Files", file);
            const fileDetail = await $api<BOMImportFileDetail>("bom/upload/start", { method: "POST", body });
            // PartNumField may be undefined from the API but a value is required for validation
            if (fileDetail.PartNumField === undefined) fileDetail.PartNumField = "";
            return fileDetail;
        } catch (err) {
            const fetchError = err as FetchError<{ Error: string }>;
            throw new Error(fetchError.data?.Error ?? t("Global.GenericError"));
        }
    }

    async function uploadComplete(bomImportFile: MaybeRefOrGetter<BOMImportFileDetail | null | undefined>) {
        const body = toValue(bomImportFile);
        if (!body) return;

        const response = await $api<UploadCompleteResponse>("bom/upload/complete", { method: "POST", body });
        if (response.res) {
            response.res.Parts.forEach((part) => (part.Selections = [])); // clear out any selections from API
        }
        return response;
    }

    function setBOMLock(bomSearch: BOMSearch | null, isLocked: boolean) {
        if (
            bomSearch &&
            isAuthenticated.value &&
            (bomSearch.BOMPermissionLevel == BOMPermissionLevel.Editor ||
                bomSearch.BOMPermissionLevel == BOMPermissionLevel.Owner)
        ) {
            return $api<{ LockOwner: string }>(`bom/${encodeURI(bomSearch.HashId)}/lock`, {
                method: isLocked ? "PUT" : "DELETE",
            });
        }
    }

    const lockBOM = (bomSearch: BOMSearch | null) => setBOMLock(bomSearch, true);

    const unlockBOM = (bomSearch: BOMSearch | null) => setBOMLock(bomSearch, false);

    return {
        createBOM,
        fetchAnonymousBOM,
        fetchBOMs,
        getDefaultBOM,
        importAnonymousBOM,
        lockBOM,
        partExistsInBOM,
        removeBOMPart,
        saveBOM,
        saveBOMPart,
        unlockBOM,
        uploadComplete,
        uploadStart,
    };
}
